import { useAuth, useSESDeleteDomainMutation, useSESDomainStatusQuery } from "@tellescope/tanstack-query";
import { VerifiedEmailDomain } from "@tellescope/types-models";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  cn,
  DestructiveConfirmDialog,
} from "@tellescope/ui";
import { RefreshCw, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SESDnsRecordsTable } from "./SESDnsRecordsTable";

export interface EmailDomainBlockProps {
  domain: VerifiedEmailDomain;
}

export const EmailDomainBlock = ({ domain }: EmailDomainBlockProps) => {
  const { t } = useTranslation("settings");
  const { selectedOrganization } = useAuth();
  const statusQuery = useSESDomainStatusQuery({ domain: domain.domain });

  const deleteDomainMutation = useSESDeleteDomainMutation();

  const data = statusQuery.data?.data;
  const dnsRecords = data && data.status !== "not_found" ? data.dnsRecords : undefined;
  const dkimRows = dnsRecords?.dkim ?? [];
  const mailFromRows = dnsRecords ? [dnsRecords.mailFrom.mx, dnsRecords.mailFrom.txt] : [];

  const status = data?.status ?? "not_found";

  return (
    <AccordionItem value={domain.domain} className="border last:border-b rounded-sm overflow-hidden">
      <AccordionTrigger
        chevronPosition="leading"
        className="items-center px-4 hover:no-underline hover:bg-gray-50"
        chevronDirection="right-to-down"
      >
        <div className="grow flex justify-between items-center">
          {domain.domain}
          <div className="flex gap-2 items-center">
            {statusQuery.data && (
              <Badge
                className={cn(
                  "rounded-full h-auto py-0.5 no-underline group-hover:no-underline",
                  status === "pending"
                    ? "bg-yellow-100 text-yellow-700"
                    : status === "verified"
                    ? "bg-green-200 text-green-700"
                    : status === "failed"
                    ? "bg-red-100 text-red-700"
                    : "bg-gray-100 text-gray-700"
                )}
              >
                {status === "failed"
                  ? t("Failed")
                  : status === "verified"
                  ? t("Verified")
                  : status === "pending"
                  ? t("Pending")
                  : t("Not Found")}
              </Badge>
            )}
            <Button
              size="icon"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                statusQuery.refetch();
              }}
              loading={statusQuery.isRefetching}
            >
              <RefreshCw />
            </Button>
            <DestructiveConfirmDialog
              onConfirm={() => {
                if (!selectedOrganization?.id) return;
                deleteDomainMutation.mutateAsync({
                  domain: domain.domain,
                });
              }}
              description={t("This will permanently remove the email domain. This action cannot be undone.")}
            >
              <Button
                size="icon"
                variant="ghost"
                className="text-destructive hover:bg-destructive/20 hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Trash2 />
              </Button>
            </DestructiveConfirmDialog>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="border-t bg-gray-50 p-4 space-y-6">
        <div className="space-y-2">
          <h4 className="text-lg">{t("MAIL FROM Records (Recommended)")}</h4>
          <SESDnsRecordsTable records={mailFromRows} />
        </div>
        <div className="space-y-2">
          <h4 className="text-lg">{t("DKIM Records (Required for sending)")}</h4>
          <SESDnsRecordsTable records={dkimRows} />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
